import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Global, mount-once helper that makes every list / report table behave
 * consistently, without editing each page:
 *
 *  - Tags read-only data tables so tableEnhancements.css can clamp their
 *    rows to a uniform height. Tables that contain form inputs (edit /
 *    data-entry tables) are left alone.
 *  - When a clamped/clipped cell is clicked, shows a dialog with the full
 *    cell text. Nothing happens if the cell isn't actually clipped.
 *
 * Works for both material-react-table (MUI Table) and MUI X DataGrid.
 *
 * Cells that build their own richer "show full info" UI (e.g. the ordered
 * products / payments columns in OrdersList.jsx, which open a proper table
 * dialog) mark themselves with `data-lmggt-handled="true"` so this generic
 * handler skips them instead of opening a second, plain-text dialog on top.
 */

// Clicks that land on these should do their own thing, not open a dialog.
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], ' +
  ".MuiCheckbox-root, .MuiRadio-root, .MuiSwitch-root, .MuiChip-root, svg, " +
  "[data-lmggt-handled]";

// A cell is "openable" only if it lives in a table we've tagged.
const CELL_SELECTOR =
  ".lmggt-clamp .MuiTableCell-root, .lmggt-datatable .MuiDataGrid-cell";

export default function TableCellExpander() {
  const [dialog, setDialog] = useState({ open: false, content: "" });

  // Mark which tables should get consistent (clamped) row heights.
  const tagTables = useCallback(() => {
    // DataGrid rows are already a fixed height — just enable click-to-open.
    document
      .querySelectorAll(".MuiDataGrid-root")
      .forEach((el) => el.classList.add("lmggt-datatable"));

    // MUI/MRT tables: clamp only when the body has no editable controls.
    document.querySelectorAll(".MuiTableContainer-root").forEach((container) => {
      const isEditable = container.querySelector(
        'tbody input, tbody textarea, tbody select, tbody [contenteditable="true"]'
      );
      container.classList.toggle("lmggt-clamp", !isEditable);
    });
  }, []);

  // Re-tag whenever the DOM changes (tables load asynchronously / on route change).
  useEffect(() => {
    tagTables();
    let scheduled = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(scheduled);
      scheduled = requestAnimationFrame(tagTables);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(scheduled);
    };
  }, [tagTables]);

  // Open the full value when a clipped cell is clicked.
  useEffect(() => {
    const handleClick = (event) => {
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;

      const cell = event.target.closest(CELL_SELECTOR);
      if (!cell) return;

      // Only react when the content is actually clipped.
      const isClipped =
        cell.scrollWidth > cell.clientWidth + 1 ||
        cell.scrollHeight > cell.clientHeight + 1;
      if (!isClipped) return;

      const content = (cell.innerText || "").trim();
      if (!content) return;

      setDialog({ open: true, content });
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleClose = () => setDialog({ open: false, content: "" });

  return (
    <Dialog open={dialog.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Full details
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {dialog.content}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
